/** Phase 5A — SCR-MER-001–008 merchant onboarding bindings (07-02) */

export const ONBOARDING_SCREEN_IDS = [
  'SCR-MER-001',
  'SCR-MER-002',
  'SCR-MER-003',
  'SCR-MER-004',
  'SCR-MER-005',
  'SCR-MER-006',
  'SCR-MER-007',
  'SCR-MER-008',
] as const;

export type OnboardingScreenId = (typeof ONBOARDING_SCREEN_IDS)[number];

export const ONBOARDING_ROUTES: Record<OnboardingScreenId, string> = {
  'SCR-MER-001': '/merchant-sign-up',
  'SCR-MER-002': '/onboarding-checklist',
  'SCR-MER-003': '/business-details',
  'SCR-MER-004': '/verification-upload',
  'SCR-MER-005': '/verification-status',
  'SCR-MER-006': '/verification-rejected',
  'SCR-MER-007': '/payout-setup',
  'SCR-MER-008': '/commercial-terms',
};

export const ONBOARDING_PERMISSIONS: Partial<Record<OnboardingScreenId, readonly string[]>> = {
  'SCR-MER-002': ['PERM-tenant.business.read'],
  'SCR-MER-003': ['PERM-tenant.business.update'],
  'SCR-MER-008': ['PERM-tenant.terms.accept', 'PERM-tenant.terms.read'],
};

/** Adyen hosted onboarding is the single merchant verification and payout-destination setup. */
export const BEAT_PAY_ONBOARDING_PERMISSION = 'PERM-beatpay.onboarding.manage';

export const BEAT_PAY_ONBOARDING_ENDPOINTS = [
  'GET /merchant/v1/onboarding/beat-pay/status',
  'POST /merchant/v1/onboarding/beat-pay/start',
  'POST /merchant/v1/onboarding/beat-pay/link',
  'POST /merchant/v1/onboarding/beat-pay/refresh',
] as const;

/** Inventory ○ poll interval for SCR-MER-002 and SCR-MER-005. */
export const ONBOARDING_POLL_INTERVAL_MS = 15_000;

export const ONBOARDING_ENDPOINTS: Partial<Record<OnboardingScreenId, readonly string[]>> = {
  'SCR-MER-001': ['POST /merchant/v1/auth/register', 'POST /merchant/v1/onboarding/applications'],
  'SCR-MER-002': [
    'GET /merchant/v1/onboarding/progress',
    'GET /merchant/v1/onboarding/requirements',
    'GET /merchant/v1/onboarding/applications/{id}',
    'GET /merchant/v1/branches',
    'GET /merchant/v1/branches/{id}',
    'GET /merchant/v1/branches/{id}/opening-hours',
    'GET /merchant/v1/branches/{id}/service-area',
    'GET /merchant/v1/branches/{id}/activation-readiness',
    'GET /merchant/v1/products',
    'POST /merchant/v1/branches/{id}/go-live',
  ],
  'SCR-MER-003': [
    'GET /merchant/v1/business',
    'PATCH /merchant/v1/business',
    'PATCH /merchant/v1/onboarding/applications/{id}',
    'POST /merchant/v1/onboarding/applications/{id}/submit',
  ],
  'SCR-MER-008': [
    'GET /merchant/v1/business/commercial-terms',
    'POST /merchant/v1/business/commercial-terms/{id}/accept',
    'POST /merchant/v1/auth/mfa/verify',
  ],
};

export const ONBOARDING_CHECKLIST_STEPS = [
  {
    code: 'business-details',
    label: 'Business details',
    route: '/business-details',
    screenId: 'SCR-MER-003',
  },
  {
    code: 'commercial-terms',
    label: 'Commercial terms',
    route: '/commercial-terms',
    screenId: 'SCR-MER-008',
  },
  {
    code: 'beat-pay-setup',
    label: 'Payments & verification',
    route: '/beat-pay-setup',
    screenId: 'SCR-MER-101',
  },
  {
    code: 'create-branch',
    label: 'Create your branch',
    route: '/create-branch',
    screenId: 'SCR-MER-011',
  },
  {
    code: 'fulfilment-settings',
    label: 'Fulfilment settings',
    route: '/fulfilment-settings',
    screenId: 'SCR-MER-016',
  },
  {
    code: 'opening-hours',
    label: 'Opening hours',
    route: '/opening-hours',
    screenId: 'SCR-MER-013',
  },
  {
    code: 'service-area',
    label: 'Delivery policy',
    route: '/service-area',
    screenId: 'SCR-MER-015',
  },
  {
    code: 'catalogue',
    label: 'Catalogue',
    route: '/product-list',
    screenId: 'SCR-MER-022',
  },
  { code: 'go-live', label: 'Go live', route: '/branch-status', screenId: 'SCR-MER-018' },
] as const;

export const ONBOARDING_PHASES = [
  {
    key: 'business',
    label: 'Business',
    description: 'Your legal details and commercial agreement',
    stepCodes: ['business-details', 'commercial-terms'],
  },
  {
    key: 'payments',
    label: 'Payments',
    description: 'Payment acceptance, verification and payouts',
    stepCodes: ['beat-pay-setup'],
  },
  {
    key: 'store-setup',
    label: 'Store setup',
    description: 'Your branch, fulfilment and catalogue',
    stepCodes: [
      'create-branch',
      'fulfilment-settings',
      'opening-hours',
      'service-area',
      'catalogue',
    ],
  },
  {
    key: 'review-launch',
    label: 'Review & launch',
    description: 'Resolve blockers and open for orders',
    stepCodes: ['go-live'],
  },
] as const;

/** 10-05a `vertical` enum. Live MOD-005 falls back to the restaurant matrix for unknown keys. */
export const MERCHANT_VERTICALS = [
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'FLORIST', label: 'Florist' },
  { value: 'CONVENIENCE', label: 'Convenience store' },
  { value: 'ELECTRONICS', label: 'Electronics' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'PET', label: 'Pet' },
] as const;

/**
 * 10-05a companyType enum. `LIMITED` is the live MOD-005 value for limited companies
 * (`LIMITED_COMPANY` in the contract).
 */
export const MERCHANT_COMPANY_TYPES = [
  { value: 'LIMITED', label: 'Limited company' },
  { value: 'SOLE_TRADER', label: 'Sole trader' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'LLP', label: 'Limited liability partnership' },
  { value: 'CHARITY', label: 'Charity' },
] as const;

export type MerchantCompanyType = (typeof MERCHANT_COMPANY_TYPES)[number]['value'];
export type MerchantVerticalValue = (typeof MERCHANT_VERTICALS)[number]['value'];

const DEFAULT_VERTICAL = MERCHANT_VERTICALS[0]?.value ?? 'RESTAURANT';

export function merchantVerticalLabel(value: string): string {
  return MERCHANT_VERTICALS.find((row) => row.value === value)?.label ?? value;
}

export function businessVerticalCodes(
  business?: {
    verticals?: unknown;
    vertical?: unknown;
    verticalCode?: unknown;
    primaryVertical?: unknown;
  } | null,
): string[] {
  const fromList = Array.isArray(business?.verticals)
    ? business.verticals.filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      )
    : [];
  if (fromList.length > 0) return [...new Set(fromList.map((value) => value.trim().toUpperCase()))];
  const single = [business?.vertical, business?.verticalCode, business?.primaryVertical].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );
  return [single?.trim().toUpperCase() ?? DEFAULT_VERTICAL];
}

export function formatVerticalLabels(codes: string[]): string {
  const labels = codes.map(merchantVerticalLabel);
  if (labels.length === 0) return merchantVerticalLabel(DEFAULT_VERTICAL);
  if (labels.length === 1) return labels[0] ?? merchantVerticalLabel(DEFAULT_VERTICAL);
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** Entity types that require a Companies House number (BR-VAL-018). */
export const COMPANY_NUMBER_REQUIRED_ENTITY_TYPES = new Set([
  'LIMITED',
  'LTD',
  'PLC',
  'LLP',
  'LIMITED_COMPANY',
  'PRIVATE_LIMITED',
  'PUBLIC_LIMITED',
]);

export const SIGNUP_MARKETING_COPY = {
  headline: 'What you get with BeatQ Eats Merchant',
  commissionNote: 'Commission applies to merchandise after discounts, before fees and tax.',
} as const;

export const MERCHANT_ONBOARDING_SUPPORT_EMAIL = 'merchants@beatqeats.co.uk';

/** Rough minutes shown on checklist steps (07-05 estimate). */
export const CHECKLIST_STEP_ESTIMATE_MINUTES: Record<string, number> = {
  'business-details': 10,
  'commercial-terms': 5,
  'beat-pay-setup': 10,
  'create-branch': 20,
  'opening-hours': 10,
  'service-area': 15,
  'fulfilment-settings': 5,
  catalogue: 30,
  'go-live': 2,
};

/** One-line purpose copy on checklist rows (07-05 §16). */
export const CHECKLIST_STEP_WHY_NEEDED: Partial<Record<string, string>> = {
  'business-details': 'We need your legal entity on file before you can set up payments.',
  'beat-pay-setup':
    'Complete identity checks and choose your payout destination securely with our payment partner.',
  'commercial-terms': 'Commission and fees were visible before sign-up, confirm them here.',
  'create-branch': 'Customers order from a branch location, start with your first site.',
  'opening-hours': 'Orders are only accepted while you are open.',
  'service-area': 'BeatQ checks every delivery against the platform-wide 3-mile road policy.',
  'fulfilment-settings': 'Choose delivery, collection, or both for this branch.',
  catalogue: 'Publish at least one product before you open for orders.',
  'go-live': 'Opens this branch to real customer orders on BeatQ Eats.',
};

/** Example values for onboarding inputs — labels remain primary (07-01). */
export const ONBOARDING_FIELD_PLACEHOLDERS = {
  businessName: 'e.g. The Corner Kitchen',
  postcode: 'e.g. EH1 1AA',
  contactName: 'e.g. Alex Morgan',
  contactEmail: 'you@yourbusiness.co.uk',
  password: 'At least 8 characters',
  legalName: 'e.g. Corner Kitchen Ltd',
  companyNumber: '12345678',
  vatNumber: 'GB123456789',
  registeredAddress: 'e.g. 12 High Street, Edinburgh',
  tradingAddress: 'e.g. 12 High Street, Edinburgh',
  tradingName: 'e.g. The Corner Kitchen',
  billingEmail: 'billing@yourbusiness.co.uk',
  supportPhone: '+44 7700 900123',
  description: 'Short description shown to customers',
  branchName: 'e.g. Leith High Street',
  branchAddress: 'e.g. 42 Constitution Street',
  branchCity: 'e.g. Edinburgh',
  defaultPrepMinutes: 'e.g. 25',
  serviceRadius: 'e.g. 3000',
  productName: 'e.g. Margherita pizza',
  productPrice: 'e.g. 9.50',
  productDescription: 'Short description customers will see',
  categoryName: 'e.g. Mains',
} as const;

export const APPLICATION_ID_STORAGE_KEY = 'beatq.merchant.applicationId';
export const RESUME_TOKEN_STORAGE_KEY = 'beatq.merchant.resumeToken';
export const CHECKLIST_VISIT_SNAPSHOT_KEY = 'beatq.merchant.checklist.snapshot';

/** Format-only company number hint — 10-05a has no registry lookup, only ERR-VALD-1022 on save. */
export const COMPANY_NUMBER_FORMAT_HINT =
  'Required for limited companies. Eight digits, or two letters and six digits. We check the format when you save.';
